import { useState, type FormEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import HoneypotField from '../components/HoneypotField'
import LangLink from '../components/LangLink'
import Section from '../components/Section'
import { sendQuoteRequest } from '../api/contact'
import { isRateLimited } from '../api/errors'

interface TitledItem {
  title: string
  body: string
}

// The 11 steps grouped into 4 phases: [start, end) indices into order.steps.
const PHASE_RANGES: [number, number][] = [
  [0, 3],
  [3, 5],
  [5, 7],
  [7, 11],
]

const EMPTY = {
  product: '',
  grade: '',
  quantity: '',
  address: '',
  company: '',
  vat: '',
  email: '',
  message: '',
  website: '', // honeypot
}

function OrderPage() {
  const { t } = useTranslation()
  const steps = t('order.steps', { returnObjects: true }) as TitledItem[]
  const stepsShort = t('order.stepsShort', { returnObjects: true }) as string[]
  const phases = t('order.phases', { returnObjects: true }) as string[]
  const callouts = t('order.callouts', { returnObjects: true }) as TitledItem[]

  const [form, setForm] = useState(EMPTY)
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error' | 'rate-limited'
  >('idle')
  const [mountedAt] = useState(() => Date.now())

  function update<K extends keyof typeof EMPTY>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setStatus('sending')
    try {
      await sendQuoteRequest({ ...form, elapsed_ms: Date.now() - mountedAt })
      setStatus('sent')
    } catch (err) {
      setStatus(isRateLimited(err) ? 'rate-limited' : 'error')
    }
  }

  const submitted = status === 'sent'

  return (
    <div className="order-page">
      {/* ---------- header ---------- */}
      <Section background="surface" space="md" className="order-hero">
        <p className="eyebrow">{t('order.eyebrow')}</p>
        <h1 className="display">{t('order.title')}</h1>
        <p className="order-lead">{t('order.intro')}</p>
        <p className="order-headline">{t('order.headline')}</p>
      </Section>

      {/* ---------- the process: 4 phases, numbered timeline ---------- */}
      <Section background="beige" space="lg" className="order-process">
        {phases.map((phaseTitle, pi) => {
          const [start, end] = PHASE_RANGES[pi]
          return (
            <div key={phaseTitle} className="order-phase">
              <p className="order-phase-tag">
                {t('order.phaseTag', { from: start + 1, to: end })}
              </p>
              <h2>{phaseTitle}</h2>
              <ol className="order-timeline">
                {steps.slice(start, end).map((step, i) => {
                  const n = start + i + 1
                  return (
                    <li key={step.title}>
                      <span className="order-step-num">
                        <svg
                          className="order-step-hex"
                          viewBox="0 0 40 40"
                          aria-hidden="true"
                        >
                          <polygon points="12,2.5 28,2.5 38,20 28,37.5 12,37.5 2,20" />
                        </svg>
                        {String(n).padStart(2, '0')}
                      </span>
                      <h3>{step.title}</h3>
                      <p>{stepsShort[n - 1]}</p>
                    </li>
                  )
                })}
              </ol>
            </div>
          )
        })}
      </Section>

      {/* ---------- order terms at a glance (dark band) ---------- */}
      <Section background="dark" space="lg" className="order-terms">
        <h2>{t('order.termsHeading')}</h2>
        <div className="order-terms-grid">
          {callouts.map((c) => (
            <div key={c.title} className="order-term">
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- quote form ---------- */}
      <Section background="beige" space="lg" className="order-quote" id="quote">
        <h2>{t('order.quoteHeading')}</h2>
        {submitted ? (
          <p className="order-sent">{t('order.sentMessage')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="form form-wide">
            <label>
              {t('order.form.product')}
              <input
                value={form.product}
                onChange={(e) => update('product', e.target.value)}
                placeholder={t('order.form.productPlaceholder')}
                required
              />
            </label>
            <label>
              {t('order.form.grade')}
              <input
                value={form.grade}
                onChange={(e) => update('grade', e.target.value)}
                placeholder={t('order.form.gradePlaceholder')}
              />
            </label>
            <label>
              {t('order.form.quantity')}
              <input
                value={form.quantity}
                onChange={(e) => update('quantity', e.target.value)}
                placeholder={t('order.form.quantityPlaceholder')}
                required
              />
            </label>
            <label>
              {t('order.form.address')}
              <input
                value={form.address}
                onChange={(e) => update('address', e.target.value)}
                required
              />
            </label>
            <label>
              {t('order.form.company')}
              <input
                value={form.company}
                onChange={(e) => update('company', e.target.value)}
                required
              />
            </label>
            <label>
              {t('order.form.vat')}
              <input
                value={form.vat}
                onChange={(e) => update('vat', e.target.value)}
                required
              />
            </label>
            <label>
              {t('order.form.email')}
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                required
              />
            </label>
            <label>
              {t('order.form.message')}
              <textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                rows={4}
              />
            </label>
            <HoneypotField
              value={form.website}
              onChange={(v) => update('website', v)}
            />
            {status === 'error' && (
              <p className="error">{t('order.form.error')}</p>
            )}
            {status === 'rate-limited' && (
              <p className="error">{t('order.form.rateLimited')}</p>
            )}
            <button
              type="submit"
              className="btn btn--primary"
              disabled={status === 'sending'}
            >
              {status === 'sending'
                ? t('order.form.submitting')
                : t('order.form.submit')}
            </button>
          </form>
        )}

        <p className="order-outro">
          <Trans
            i18nKey="order.outro"
            components={{ lnk: <LangLink to="/products" /> }}
          />
        </p>
      </Section>
    </div>
  )
}

export default OrderPage
